import { useEffect, useState, useCallback } from 'react';
import {
  ShieldCheck, Lock, Loader2, Smartphone, KeyRound, Fingerprint,
  CheckCircle2, AlertCircle, Copy, Upload, Clock, RefreshCw,
} from 'lucide-react';
import api, { formatApiError } from '@/lib/api';
import {
  isWebAuthnAvailable, isPlatformAuthenticatorAvailable,
  startRegistration, startAuthentication,
} from '@/lib/webauthnClient';

const NAVY = '#16335b';

/* Compress an uploaded image to a small JPEG data-URL (proof of UPI payment). */
function compressImage(file, maxW = 1000, quality = 0.7) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onload = () => { img.src = reader.result; };
    reader.onerror = reject;
    img.onload = () => {
      const scale = Math.min(1, maxW / img.width);
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    img.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function PinField({ value, onChange, label, testid }) {
  return (
    <div>
      <label className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500">{label}</label>
      <input
        type="password" inputMode="numeric" autoComplete="one-time-code" maxLength={6}
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, 6))}
        placeholder="••••••"
        className="w-full mt-1.5 text-center tracking-[0.6em] text-2xl font-bold py-3 rounded-2xl border border-slate-300 focus:border-sky-500 focus:ring-2 focus:ring-sky-200 outline-none"
        data-testid={testid}
      />
    </div>
  );
}

function Shell({ icon, overline, title, children }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-5" style={{ background: '#e7ddc9', fontFamily: 'Manrope, sans-serif' }}>
      <div className="max-w-md w-full bg-white rounded-3xl border border-[#e0d6c0] p-7 shadow-xl">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-sky-50 border border-sky-100 mb-3">{icon}</div>
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-sky-700">{overline}</p>
        <h1 className="text-xl font-bold text-slate-900 mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{title}</h1>
        <div className="mt-4">{children}</div>
        <p className="text-[10px] text-slate-400 mt-5 text-center flex items-center justify-center gap-1">
          <ShieldCheck size={11} /> Zero-trust · encrypted · DocSite&trade;
        </p>
      </div>
    </div>
  );
}

export default function DhfAccessGate({ token, dhf, onUnlocked, optIn = false }) {
  const [phase, setPhase] = useState('init');        // init|enroll|unlock|paywall|forgot|device
  const [gate, setGate] = useState(null);            // challenge response
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [info, setInfo] = useState('');

  // shared inputs
  const [maskedPhone, setMaskedPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSession, setOtpSession] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [pin, setPin] = useState('');
  const [pin2, setPin2] = useState('');
  const [step, setStep] = useState('phone');         // phone|otp|pin (within enroll/recovery)
  const [upiStatus, setUpiStatus] = useState(dhf?.paywall?.upi_status || 'none');

  const runChallenge = useCallback(async () => {
    setErr('');
    try {
      const { data } = await api.post(`/public/health-file/${token}/challenge`);
      setGate(data);
      if (data.status === 'NEEDS_ENROLLMENT') { setPhase('enroll'); setStep('phone'); }
      else if (data.status === 'LOCKED') { setPhase('unlock'); }
      else if (data.status === 'LEGACY') {
        if (optIn) { setPhase('enroll'); setStep('phone'); }   // opt-in lock from banner
        else setPhase('legacy');
      }
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || 'Could not load security challenge');
    }
  }, [token, optIn]);

  useEffect(() => { runChallenge(); }, [runChallenge]);

  const sendOtp = async () => {
    setBusy(true); setErr('');
    try {
      const { data } = await api.post(`/public/health-file/${token}/otp/send`);
      setMaskedPhone(data.masked_phone || '');
      setOtpSent(true); setStep('otp');
      setInfo(`OTP sent to ${data.masked_phone || 'your phone'}`);
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || 'Could not send OTP'); }
    finally { setBusy(false); }
  };

  const verifyOtp = async () => {
    setBusy(true); setErr('');
    try {
      const { data } = await api.post(`/public/health-file/${token}/otp/verify`, { otp });
      setOtpSession(data.otp_session);
      setStep('pin'); setInfo('Phone verified ✓');
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || 'Incorrect OTP'); }
    finally { setBusy(false); }
  };

  const handleUnlocked = (payload) => {
    if (payload.paywalled) { setPhase('paywall'); setUpiStatus(payload.paywall?.upi_status || 'none'); setGate(payload); }
    else onUnlocked(payload, pin);
  };

  // ── Enrollment: create PIN + (optional) passkey ──
  const completeEnrollment = async () => {
    if (pin.length !== 6) { setErr('Enter a 6-digit PIN'); return; }
    if (pin !== pin2) { setErr('PINs do not match'); return; }
    setBusy(true); setErr('');
    let attestation = null;
    try {
      if (isWebAuthnAvailable() && await isPlatformAuthenticatorAvailable()) {
        try {
          const { data: opt } = await api.post(`/public/health-file/${token}/enroll/options`, { otp_session: otpSession });
          attestation = await startRegistration(opt.registration_options);
        } catch (we) {
          if (we?.name !== 'NotSupportedError' && we?.name !== 'NotAllowedError' && we?.name !== 'AbortError') {
            // non-fatal — fall through to PIN-only
          }
          attestation = null;   // graceful fallback to PIN + SMS OTP
        }
      }
      const { data } = await api.post(`/public/health-file/${token}/verify-enrollment`, {
        otp_session: otpSession, pin, attestation,
      });
      handleUnlocked(data);
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || 'Enrollment failed'); }
    finally { setBusy(false); }
  };

  // ── Unlock: PIN (+ passkey if hardware-bound) ──
  const completeUnlock = async () => {
    if (pin.length !== 6) { setErr('Enter your 6-digit PIN'); return; }
    setBusy(true); setErr('');
    let assertion = null;
    try {
      if (gate?.hardware_bound) {
        try {
          assertion = await startAuthentication(gate.authentication_options);
        } catch {
          setErr('Passkey check was cancelled. Tap unlock to try again, or use "Use a different device".');
          setBusy(false); return;
        }
      }
      const { data } = await api.post(`/public/health-file/${token}/verify-assertion`, { pin, assertion });
      handleUnlocked(data);
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || 'Could not unlock'); }
    finally { setBusy(false); }
  };

  // ── Recovery: forgot PIN ──
  const completeForgotPin = async () => {
    if (pin.length !== 6 || pin !== pin2) { setErr('Enter a matching 6-digit PIN'); return; }
    setBusy(true); setErr('');
    let assertion = null;
    try {
      if (gate?.hardware_bound) {
        const { data: ch } = await api.post(`/public/health-file/${token}/challenge`);
        assertion = await startAuthentication(ch.authentication_options);
      }
      await api.post(`/public/health-file/${token}/recover-pin`, { otp_session: otpSession, new_pin: pin, assertion });
      setInfo('PIN reset ✓ Use your new PIN to open this file.');
      setPin(''); setPin2(''); setOtp(''); setOtpSession(''); setOtpSent(false);
      await runChallenge(); setPhase('unlock');
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || 'Could not reset PIN'); }
    finally { setBusy(false); }
  };

  // ── Recovery: changed device ──
  const completeRecoverDevice = async () => {
    setBusy(true); setErr('');
    try {
      await api.post(`/public/health-file/${token}/recover-device`, { otp_session: otpSession, pin });
      setInfo('Old device unlinked. Re-enroll this device below.');
      setPin(''); setPin2(''); setOtp(''); setOtpSession(''); setOtpSent(false);
      setPhase('enroll'); setStep('pin');   // OTP session already verified → straight to PIN/passkey
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || 'Could not switch device'); }
    finally { setBusy(false); }
  };

  // ── Manual UPI ──
  const [uploading, setUploading] = useState(false);
  const submitUpi = async (file) => {
    setUploading(true); setErr('');
    try {
      const proof = await compressImage(file);
      const { data } = await api.post(`/public/health-file/${token}/upi/submit`, { proof_data_url: proof });
      setUpiStatus(data.status || 'pending_review');
      setInfo(data.message || 'Payment proof received.');
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || 'Upload failed'); }
    finally { setUploading(false); }
  };
  const checkUpi = async () => {
    const { data } = await api.get(`/public/health-file/${token}/upi/status`);
    setUpiStatus(data.upi_status);
    if (!data.paywalled) { setInfo('Payment confirmed ✓ unlocking…'); await runChallenge(); setPhase('unlock'); }
    else setInfo('Still pending doctor confirmation.');
  };

  const Banner = () => (
    <>
      {err && <div className="mt-3 flex items-start gap-2 text-[13px] text-rose-700 bg-rose-50 border border-rose-200 rounded-xl p-2.5" data-testid="dhf-gate-error"><AlertCircle size={15} className="mt-0.5 shrink-0" />{err}</div>}
      {info && !err && <div className="mt-3 flex items-start gap-2 text-[13px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl p-2.5"><CheckCircle2 size={15} className="mt-0.5 shrink-0" />{info}</div>}
    </>
  );

  const primaryBtn = (label, onClick, icon) => (
    <button onClick={onClick} disabled={busy}
      className="w-full mt-4 py-3.5 rounded-full text-white text-sm font-bold hover:opacity-90 disabled:opacity-60 flex items-center justify-center gap-2"
      style={{ background: NAVY }} data-testid="dhf-gate-primary">
      {busy ? <Loader2 size={16} className="animate-spin" /> : icon}{label}
    </button>
  );

  // OTP / phone sub-step shared by enrollment + recovery.
  // NOTE: rendered via a function call (not a nested <Component/>) so the OTP
  // input keeps its DOM identity across keystrokes and never loses focus.
  const renderOtpStep = (onVerified) => (
    <>
      {!otpSent ? (
        <>
          <p className="text-sm text-slate-600">For your security we'll text a one-time code to the mobile number on file for this health file.</p>
          {primaryBtn('Send OTP to my phone', sendOtp, <Smartphone size={16} />)}
        </>
      ) : (
        <>
          <p className="text-sm text-slate-600">Enter the 6-digit code sent to <b>{maskedPhone}</b>.</p>
          <input value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 8))}
            inputMode="numeric" placeholder="Enter OTP"
            className="w-full mt-3 text-center tracking-[0.3em] text-lg font-bold py-3 rounded-2xl border border-slate-300 focus:border-sky-500 outline-none"
            data-testid="dhf-gate-otp" />
          {primaryBtn('Verify', () => onVerified ? onVerified() : verifyOtp(), <KeyRound size={16} />)}
          <button onClick={sendOtp} disabled={busy} className="w-full mt-2 text-[12px] text-slate-500 hover:text-slate-700">Resend code</button>
        </>
      )}
    </>
  );

  // ───────────────────────── render ─────────────────────────
  if (phase === 'init') {
    return <Shell icon={<Loader2 className="animate-spin" style={{ color: NAVY }} />} overline="DocSite Secure" title="Checking security…"><Banner /></Shell>;
  }

  if (phase === 'enroll') {
    return (
      <Shell icon={<ShieldCheck size={22} className="text-sky-600" />} overline="One-time setup" title="Lock this file to your device">
        {step === 'phone' && renderOtpStep()}
        {step === 'otp' && renderOtpStep()}
        {step === 'pin' && (
          <>
            <p className="text-sm text-slate-600">Create a 6-digit Master PIN. You'll use it (with Face ID / fingerprint where available) every time you open this file.</p>
            <div className="mt-3 space-y-3">
              <PinField label="Create PIN" value={pin} onChange={setPin} testid="dhf-gate-pin" />
              <PinField label="Confirm PIN" value={pin2} onChange={setPin2} testid="dhf-gate-pin2" />
            </div>
            <p className="text-[11px] text-slate-400 mt-2 flex items-start gap-1"><Fingerprint size={13} className="mt-0.5 shrink-0" /> If you forget this PIN your offline copy clears for security, but your cloud record stays safe.</p>
            {primaryBtn('Secure & open my file', completeEnrollment, <Lock size={16} />)}
          </>
        )}
        <Banner />
      </Shell>
    );
  }

  if (phase === 'unlock') {
    return (
      <Shell icon={<Lock size={22} className="text-sky-600" />} overline="Protected health file" title={`Unlock ${dhf?.patient?.name || 'your file'}`}>
        <p className="text-sm text-slate-600">{gate?.hardware_bound ? 'Confirm with your device (Face ID / fingerprint) and enter your 6-digit PIN.' : 'Enter your 6-digit Master PIN.'}</p>
        <div className="mt-3"><PinField label="Master PIN" value={pin} onChange={setPin} testid="dhf-gate-pin" /></div>
        {primaryBtn(gate?.hardware_bound ? 'Unlock with passkey + PIN' : 'Unlock', completeUnlock, gate?.hardware_bound ? <Fingerprint size={16} /> : <KeyRound size={16} />)}
        <div className="mt-3 flex items-center justify-between text-[12px]">
          <button onClick={() => { setPhase('forgot'); setStep('phone'); setOtpSent(false); setPin(''); setPin2(''); setErr(''); }} className="text-slate-500 hover:text-slate-800" data-testid="dhf-gate-forgot-pin">Forgot PIN?</button>
          <button onClick={() => { setPhase('device'); setStep('phone'); setOtpSent(false); setErr(''); }} className="text-slate-500 hover:text-slate-800" data-testid="dhf-gate-device-changed">Use a different device</button>
        </div>
        <Banner />
      </Shell>
    );
  }

  if (phase === 'forgot') {
    return (
      <Shell icon={<RefreshCw size={20} className="text-sky-600" />} overline="Reset PIN" title="Forgot your PIN?">
        {step !== 'pin' ? renderOtpStep(async () => { await verifyOtp(); }) : (
          <>
            <p className="text-sm text-slate-600">{gate?.hardware_bound ? 'Set a new PIN — your device passkey will confirm it.' : 'Set a new 6-digit PIN.'}</p>
            <div className="mt-3 space-y-3">
              <PinField label="New PIN" value={pin} onChange={setPin} testid="dhf-gate-pin" />
              <PinField label="Confirm new PIN" value={pin2} onChange={setPin2} testid="dhf-gate-pin2" />
            </div>
            {primaryBtn('Reset PIN', completeForgotPin, <KeyRound size={16} />)}
          </>
        )}
        <button onClick={() => { setPhase('unlock'); setErr(''); setOtpSent(false); }} className="w-full mt-3 text-[12px] text-slate-500 hover:text-slate-800">← Back</button>
        <Banner />
      </Shell>
    );
  }

  if (phase === 'device') {
    return (
      <Shell icon={<Smartphone size={20} className="text-sky-600" />} overline="New device" title="Switch to this device">
        {step !== 'pin' ? renderOtpStep(async () => { await verifyOtp(); }) : (
          <>
            <p className="text-sm text-slate-600">Confirm your current Master PIN to unlink the old device and register this one.</p>
            <div className="mt-3"><PinField label="Current PIN" value={pin} onChange={setPin} testid="dhf-gate-pin" /></div>
            {primaryBtn('Unlink old device', completeRecoverDevice, <RefreshCw size={16} />)}
          </>
        )}
        <button onClick={() => { setPhase('unlock'); setErr(''); setOtpSent(false); }} className="w-full mt-3 text-[12px] text-slate-500 hover:text-slate-800">← Back</button>
        <Banner />
      </Shell>
    );
  }

  if (phase === 'paywall') {
    const pw = gate?.paywall || dhf?.paywall || {};
    const upiLink = pw.upi_vpa ? `upi://pay?pa=${pw.upi_vpa}&pn=${encodeURIComponent(pw.upi_payee || 'DocSite')}&am=${pw.price_inr || 20}&tn=${encodeURIComponent(pw.upi_note || '')}&cu=INR` : '';
    return (
      <Shell icon={<Lock size={22} className="text-amber-600" />} overline="Unlock your file" title={`Pay ₹${pw.price_inr || 20} via UPI`}>
        <p className="text-sm text-slate-600">{pw.message}</p>
        {upiStatus === 'pending_review' ? (
          <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-center" data-testid="dhf-upi-pending">
            <Clock size={22} className="text-amber-600 mx-auto mb-1.5" />
            <p className="text-sm font-bold text-amber-900">Payment under review</p>
            <p className="text-[12px] text-amber-800 mt-1">Your file unlocks once your doctor confirms the payment.</p>
            <button onClick={checkUpi} className="mt-3 px-4 py-2 rounded-full bg-white border border-amber-300 text-amber-800 text-[12px] font-semibold" data-testid="dhf-upi-refresh">Check status</button>
          </div>
        ) : (
          <>
            <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">Pay to UPI ID</p>
              {pw.upi_vpa ? (
                <div className="flex items-center justify-between gap-2 mt-1">
                  <span className="font-mono text-sm font-bold text-slate-900">{pw.upi_vpa}</span>
                  <button onClick={() => { navigator.clipboard?.writeText(pw.upi_vpa); setInfo('UPI ID copied'); }} className="text-sky-600" data-testid="dhf-upi-copy"><Copy size={15} /></button>
                </div>
              ) : <p className="text-[12px] text-slate-500 mt-1">Ask your doctor for their UPI ID to complete payment.</p>}
              <p className="text-[11px] text-slate-500 mt-1.5">Note: {pw.upi_note}</p>
              {upiLink && <a href={upiLink} className="block w-full text-center mt-3 py-2.5 rounded-full text-white text-[13px] font-bold" style={{ background: NAVY }} data-testid="dhf-upi-pay">Open UPI app to pay ₹{pw.price_inr || 20}</a>}
            </div>
            <label className="block mt-4">
              <span className="text-[12px] font-semibold text-slate-700">Upload payment screenshot to confirm</span>
              <div className="mt-2 flex items-center justify-center gap-2 py-3 rounded-2xl border-2 border-dashed border-slate-300 text-slate-500 text-[13px] cursor-pointer hover:border-sky-400">
                {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}{uploading ? 'Uploading…' : 'Choose screenshot'}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && submitUpi(e.target.files[0])} data-testid="dhf-upi-upload" />
              </div>
            </label>
            {pw.patient_turbo_upsell_message && (
              <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-xl p-2.5 mt-3">{pw.patient_turbo_upsell_message}</p>
            )}
          </>
        )}
        <Banner />
      </Shell>
    );
  }

  return null;
}
